/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Injector, Input, Output } from '@angular/core';
import { type ColumnDef, type Rest, type TableConfig, ButtonService, TableAbstract, UtilService } from '@dm/core';
import { type Button } from '@dm/core/types';
import { type NFSClient } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config';
import { FileSystemEntryE } from 'app/feature/service/file-service/file-system/create-file-system/create-file-system.component';

import { ClientType } from '../../nfs-share.enum';
import { NfsShareStrService } from '../../nfs-share-str.service';
import { AddClientNfsShareComponent } from '../add-client-nfs-share/add-client-nfs-share.component';
import { ModifyClientNfsShareComponent } from '../modify-client-nfs-share/modify-client-nfs-share.component';

@Component({
    selector: 'app-front-table-client-nfs-share',
    templateUrl: './front-table-client-nfs-share.component.html',
    styleUrls: ['./front-table-client-nfs-share.component.scss'],
})
export class FrontTableClientNfsShareComponent extends TableAbstract<any> implements OnInit {
    @Input() tableData: NFSClient[] = [];
    // tableData的双向绑定实现，每一次改变值，都要emit出去
    @Output() tableDataChange: EventEmitter<any> = new EventEmitter();
    @Input() supportSmartGui = true;
    @Input() entry: FileSystemEntryE;
    /**
     * 三步创共享中
     */
    @Input() isCopyFrom = false;
    /**
     * 租户ID
     */
    @Input() vstoreId: string;
    /**
     * 传入的文件系统
     */
    @Input() fileSystem?: FileSystem;

    i18n = this.str.i18n;

    tableRest: Rest;
    tableCountRest: Rest;
    tableConfig: TableConfig;
    tableColumnDefs: ColumnDef[];
    selection: any[] = [];

    addBtn: Button;
    modifyBtn: Button;
    removeBtn: Button;

    SWITCH = SWITCH;

    constructor(
        public str: NfsShareStrService,
        private buttonService: ButtonService,
        private utilService: UtilService,
        injector: Injector,
    ) {
        super(injector);
    }

    initBtns(): void {
        this.addBtn = this.buttonService.create({
            id: 'add-client-nfs-share',
            label: this.i18n.btn.addBtn,
            theme: 'primary',
            depPermission: ['task_W'],
            command: () => {
                this.add();
            },
        });
        this.modifyBtn = this.buttonService.create({
            id: 'modify-client-nfs-share',
            label: this.i18n.btn.modifyBtn,
            command: ({ data }) => {
                this.modify(data);
            },
        });
        this.removeBtn = this.buttonService.create({
            id: 'remove-client-nfs-share',
            label: this.i18n.btn.removeBtn,
            disabledCheck: (data) => !data && this.selection.length === 0,
            command: ({ data }) => {
                this.remove(data ? [data] : this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            dataKey: 'NAME',
            selectionMode: 'checkbox',
            beforeShow: (datas: NFSClient[]): Array<NFSClient> => {
                // 手动组装一个_type字段
                datas.forEach((data) => {
                    data._type = data.NAME.charAt(0) === '@' ? ClientType.net : ClientType.host;
                });
                return datas;
            },
            menuItems: [
                this.modifyBtn,
                this.removeBtn,
            ],
        };

        this.tableColumnDefs = [
            {
                field: 'NAME',
                header: this.i18n.label.client,
                filterType: 'search',
                render: (value: string): string => {
                    // 如果是网络组，会多一个@符号
                    if (value.charAt(0) === '@') {
                        return value.substr(1);
                    }
                    return value;
                },
            },
            {
                field: '_type',
                header: this.i18n.label.type,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.str.clientTypeOpts,
                render: (value) => this.utilService.getLabelFromOptions(this.str.clientTypeOpts, value),
            },
            {
                field: 'ACCESSVAL',
                header: this.i18n.label.unixPermissionLab,
                columnFilter: true,
                filterType: 'dropdown',
                options: this.entry === FileSystemEntryE.AuditLog ?
                    this.str.auditLogFsPermissionLevelOpts : this.str.permissionLevelOpts,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5',
                header: this.i18n.label.kerberos5PermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5I',
                header: this.i18n.label.kerberos5iPermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
            {
                field: 'ACCESSKRB5P',
                header: this.i18n.label.kerberos5pPermissionLab,
                columnFilter: true,
                hidden: true,
                options: this.str.permissionLevelOpts,
                ignore: SWITCH.IS_SIMPLE_MODE || ProductConfig.isHideKerberos,
                render: (value) => this.utilService.getLabelFromOptions(this.str.permissionLevelOpts, value),
            },
        ];
    }

    updateTableData(): void {
        this.tableDataChange.emit(this.tableData);
        this.dt.updateData(this.tableData);
    }

    add(): void {
        this.doAction(AddClientNfsShareComponent, {
            isFrontTable: true,
            clients: this.tableData,
            vstoreId: this.vstoreId,
            supportSmartGui: this.supportSmartGui,
            entry: this.entry,
            fileSystem: this.fileSystem,
            callBack: (datas: NFSClient[]) => {
                datas.forEach((value) => {
                    // 先校验重复，再添加，防止誤操作
                    const tempIndex = this.tableData.findIndex((item) => item.NAME === value.NAME);
                    if (tempIndex === -1) {
                        this.tableData.push(value);
                    }
                });

                // 用...触发angualr刷新，push触发不了
                this.tableData = [...this.tableData];
                this.updateTableData();
            },
        });
    }

    modify(data: NFSClient): void {
        this.doAction(ModifyClientNfsShareComponent, {
            data,
            isFrontTable: true,
            clients: this.tableData,
            entry: this.entry,
            fileSystem: this.fileSystem,
            callBack: (value: NFSClient) => {
                // 找到老位置
                const oldIndex = this.tableData.findIndex((item) => item.NAME === data.NAME);

                // 把老位置内容换了
                if (oldIndex !== -1) {
                    this.tableData[oldIndex] = { ...this.tableData[oldIndex], ...value };
                    this.updateTableData();
                }
            },
        });
    }

    /**
     * 前端删除
     * @param removeList 待删除列表
     */
    remove(removeList: NFSClient[]): void {
        this.tableData = this.tableData.filter((data) => {
            // 如果这个data的名字 在removeList中，那就得删掉
            const tempIndex = removeList.findIndex((removeItem) => removeItem.NAME === data.NAME);

            // filter true的留下， false的删掉
            return tempIndex === -1;
        });

        // 更改数据后，刷新表格，触发双向绑定
        this.updateTableData();
    }
}
